'use client';

import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useEffect } from 'react';

import { Policy } from '@flipster/contentful-graphql/codegen/types.generated';
import ContentfulRichText from '@flipster/features/src/shared/contentful-rich-text';
import { useCheckSpotOnly, userRegulationBunja } from '@flipster/features/src/shared/regulation';
import { useRouter } from '@flipster/i18n/routing';
import { SpinnerBox } from '@flipster/react/design-system/spinner';
import { css } from '@flipster/styled-system/css';

export const PoliciesContents = ({ policy, policies }: { policy: Policy; policies: Partial<Policy>[] }) => {
  const isSpotOnly = useCheckSpotOnly();
  const { isRegulationLoadingAtom } = useBunja(userRegulationBunja);
  const isRegulationLoading = useAtomValue(isRegulationLoadingAtom);
  const router = useRouter();

  const isRegulationMismatch = policy.isVaraRegulated !== null && policy.isVaraRegulated !== isSpotOnly;

  useEffect(() => {
    // 규제 정보 로딩이 끝나기 전에는 isSpotOnly가 기본값(false)이라 잘못된 리다이렉트가 발생하므로 대기
    if (isRegulationLoading) return;

    // 사용자 규제 상태와 정책 규제 상태가 다르면 적절한 정책으로 리다이렉트
    if (isRegulationMismatch) {
      const fallbackPolicy = policies.find((p) => p.isVaraRegulated === isSpotOnly || p.isVaraRegulated === null);
      if (fallbackPolicy?.slug) {
        router.replace(`/policies/${fallbackPolicy.slug}`);
      }
    }
  }, [isSpotOnly, isRegulationLoading, isRegulationMismatch, router, policies]);

  if (isRegulationLoading || isRegulationMismatch) {
    return (
      <SpinnerBox
        css={{
          minH: { base: '50dvh', tablet: '60dvh' },
        }}
      />
    );
  }

  return (
    <>
      <header>
        <h1
          className={css({
            textStyle: 'Display/XL',
            color: 'inverse.white',
            borderBottomWidth: '1px',
            borderBottomStyle: 'solid',
            borderBottomColor: 'border.default.level-3',
            pb: { base: 16, tablet: 20 },
            my: { base: 20, tablet: 32 },
          })}
        >
          {policy.title}
        </h1>
      </header>
      <ContentfulRichText richTextDocument={policy.content} startHeadingLevel={2} />
    </>
  );
};
